import type { BrandConfig } from "./brand.schema";

/**
 * THE white-label config default.
 *
 * This is the ONLY file in the repo allowed to contain literal brand
 * strings, hex colors, domains, or emails — `scripts/lint-brand.ts` (wired
 * into CI) fails the build on any of those appearing anywhere else in
 * `src/**`. If you need a brand value somewhere, import it from
 * `@/config/brand`, don't retype it.
 *
 * Colors are the locked design-system values (Modern Warm, 2026-07-02,
 * DESIGN_SYSTEM.md §3): `primary` = deep teal ink (`color.text.primary`),
 * `accent` = the brand teal (`color.accent.default`). They must stay
 * in sync with design/tokens/tokens.json — src/config/theme.test.ts asserts
 * it. This file is also exempt from design/checks/token-adherence.mjs for
 * the same reason it is exempt from lint:brand — it IS the raw-value home.
 */
export const defaultBrandConfig: BrandConfig = {
  schema_version: 1,
  brandName: "Mapize",
  logoUrl: "/brand/logo.svg",
  colors: {
    primary: "#123B31",
    accent: "#0FA08C",
  },
  appDomain: "mapize.com",
  supportEmail: "support@mapize.com",
  legalEntity: "Mapize, Inc.",
  legalUrls: {
    tos: "/legal/tos/",
    privacy: "/legal/privacy/",
    cookies: "/legal/cookies/",
    dpa: "/legal/dpa/",
    acceptableUse: "/legal/acceptable-use/",
  },
  attribution: {
    base: "© Mapize",
    osm: "© OpenStreetMap contributors",
    protomaps: "© Protomaps",
    // Esri World Imagery — required credit for the keyless satellite basemap
    // (MAP-102); shown only when a map's basemap is satellite.
    esri: "Imagery © Esri, Maxar, Earthstar Geographics",
    // Restricted-provider credits are added by the deployment when that
    // provider is switched on; omitted here because the default stack is
    // Protomaps + OpenStreetMap only.
  },
  legal: {
    registeredAddress: "8117 Rockport Circle, Las Vegas, NV 89128, USA",
    jurisdiction: "Delaware, USA",
    governingLaw: "the laws of the State of Delaware, USA",
    // No separate privacy inbox — privacy / data-subject requests route through support.
    privacyContact: "support@mapize.com",
    badgeLabel: "Made with Mapize",
    effectiveDates: {
      terms: "2026-07-01",
      privacy: "2026-07-01",
      cookies: "2026-07-01",
      dpa: "2026-07-01",
      acceptableUse: "2026-07-01",
    },
  },
  // M8 deep-analysis tools — ALL OFF by default so the wedge ships without them.
  // A deployment turns one on here or via the matching BRAND_ANALYSIS_* env var;
  // the M7 entitlement still gates open-time.
  analysisTools: {
    radius: false,
    territoriesLasso: false,
    territoriesAutomated: false,
    territoriesBoundary: false,
    driveTime: false,
    routes: false,
  },
  // M9 demographics/census overlays — OFF by default so the wedge ships without
  // the overlay engine exposed. Turn on here or via BRAND_DEMOGRAPHICS_OVERLAYS;
  // the M7 entitlement still gates render-time.
  demographicsOverlays: false,
  // M10 OAuth clients — client id/secret NULL by default (unconfigured). A
  // deployment supplies them via OAUTH_<CONNECTOR>_CLIENT_ID / _CLIENT_SECRET;
  // no credential is ever hardcoded here. The connector adapters ship in M10-B.
  oauth: {
    gsheet: { clientId: null, clientSecret: null },
    salesforce: { clientId: null, clientSecret: null },
    hubspot: { clientId: null, clientSecret: null },
    zoho: { clientId: null, clientSecret: null },
  },
  // M13 eval-harness gate baselines + vendor rate constants. These are the ONLY
  // home for every green/red threshold the harness reads — the eval source
  // contains no baked-in baseline, so tuning a gate is a config edit here (or a
  // white-label theme override), never a code change. `rates.rateSetDate` is
  // printed on every RunReport so a stale COGS verdict is visible.
  eval: {
    thresholds: {
      nl: {
        planSuccessMin: 0.8, // M13 AC: applied-vs-error/undo >= 80%
        misinterpretationMax: 0.15, // M13 AC: undo-within-10s proxy <= 15%
        intentAccuracyMin: 0.9,
        costPerCallCeilingUsd: 0.02,
      },
      geocode: {
        parityTargetPct: 95, // the ~95% US-parity falsifiable bet (M13-B scores it)
        cacheHitFloorPct: 70, // steady-state shared-cache hit-rate floor (M13-B)
        costPer1kCeilingUsd: 5,
      },
      render: {
        timeToFirstMapP95Ms: 30_000,
        editorLcpMs: 2_500,
        embedLcpMs: 2_000,
        interactionFpsMin: 50,
        styleFilterP95Ms: 1_500,
        queryP95Ms: 4_000,
        embedJsGzipMaxBytes: 250 * 1024,
      },
      costPerActiveMapCeilingUsd: 0.5,
    },
    // Vendor rates, dated. Placeholders reviewed on rateSetDate; a deployment
    // updates them (and the date) as vendor pricing moves.
    rates: {
      rateSetDate: "2026-07-01",
      llmCostPer1kInputTokensUsd: 0.001,
      llmCostPer1kOutputTokensUsd: 0.005,
      geocodeBlendedCostPer1kUsd: 0, // OSS-first default path (Census + Pelias)
      geocodeGoogleCostPer1kUsd: 5,
    },
  },
};
